import { Capacitor } from '@capacitor/core';
import {
  LocalNotifications,
  type DeliveredNotificationSchema,
  type LocalNotificationDescriptor,
  type LocalNotificationSchema,
} from '@capacitor/local-notifications';

const OFFLINE_REMINDER_CHANNEL_ID = 'offline-sync-reminders';
const OFFLINE_REMINDER_ID_BASE = 1_870_000;
const HOUR_MS = 60 * 60 * 1_000;

export const OFFLINE_REMINDER_HOURS = [
  1, 2, 3, 4, 5, 6,
  7, 8, 9, 10, 11, 12,
  24,
] as const;

const OFFLINE_REMINDER_IDS = OFFLINE_REMINDER_HOURS.map((_, index) => OFFLINE_REMINDER_ID_BASE + index + 1);
const offlineReminderIdSet = new Set(OFFLINE_REMINDER_IDS);
let reminderOperations: Promise<void> = Promise.resolve();

export async function initializeOfflineReminders(): Promise<void> {
  if (!isSupportedNativePlatform()) return;
  if (Capacitor.getPlatform() === 'android') {
    await LocalNotifications.createChannel({
      id: OFFLINE_REMINDER_CHANNEL_ID,
      name: 'Sincronización pendiente',
      description: 'Recordatorios para subir encuestas y evidencias guardadas sin conexión.',
      importance: 3,
      visibility: 1,
      lights: true,
      lightColor: '#0077FF',
      vibration: true,
    }).catch((error) => console.error('No fue posible crear el canal de recordatorios de sincronización.', error));
  }
  await cancelOfflineReminders();
}

export async function requestOfflineReminderPermission(): Promise<boolean> {
  if (!isSupportedNativePlatform()) return false;
  try {
    const current = await LocalNotifications.checkPermissions();
    if (current.display === 'granted') return true;
    if (current.display === 'denied') return false;
    const requested = await LocalNotifications.requestPermissions();
    return requested.display === 'granted';
  } catch (error) {
    console.error('No fue posible solicitar permiso para los recordatorios de sincronización.', error);
    return false;
  }
}

export function scheduleOfflineReminders(pendingCount: number, scheduledFrom = new Date()): Promise<void> {
  return enqueueReminderOperation(async () => {
    await cancelOfflineRemindersNow();
    if (!isSupportedNativePlatform() || pendingCount <= 0) return;

    try {
      const permission = await LocalNotifications.checkPermissions();
      if (permission.display !== 'granted') {
        console.warn('No se agendaron recordatorios de sincronización porque las notificaciones no tienen permiso.');
        return;
      }

      const notifications: LocalNotificationSchema[] = OFFLINE_REMINDER_HOURS.map((hours, index) => ({
        id: OFFLINE_REMINDER_IDS[index]!,
        title: 'Sincronización pendiente',
        body: 'Abre Eqover para subir las encuestas y evidencias guardadas en este dispositivo.',
        largeBody: 'Tienes información pendiente por falta de conexión. Abre Eqover para iniciar la sincronización.',
        summaryText: `${pendingCount} ${pendingCount === 1 ? 'registro pendiente' : 'registros pendientes'}`,
        schedule: {
          at: new Date(scheduledFrom.getTime() + hours * HOUR_MS),
          allowWhileIdle: true,
        },
        channelId: OFFLINE_REMINDER_CHANNEL_ID,
        sound: 'default',
        smallIcon: 'ic_stat_eqover',
        iconColor: '#0077FF',
        autoCancel: true,
        extra: { type: 'offline-sync-reminder' },
      }));

      await LocalNotifications.schedule({ notifications });
    } catch (error) {
      console.error('No fue posible agendar los recordatorios de sincronización.', error);
    }
  });
}

export function cancelOfflineReminders(): Promise<void> {
  return enqueueReminderOperation(cancelOfflineRemindersNow);
}

async function cancelOfflineRemindersNow(): Promise<void> {
  if (!isSupportedNativePlatform()) return;
  const notifications: LocalNotificationDescriptor[] = OFFLINE_REMINDER_IDS.map((id) => ({ id }));
  await LocalNotifications.cancel({ notifications }).catch((error) => {
    console.error('No fue posible cancelar los recordatorios pendientes de sincronización.', error);
  });

  const delivered = await LocalNotifications.getDeliveredNotifications().catch(() => ({ notifications: [] }));
  const matching = delivered.notifications.filter((notification) => offlineReminderIdSet.has(notification.id));
  if (matching.length) {
    await LocalNotifications.removeDeliveredNotifications({
      notifications: matching as DeliveredNotificationSchema[],
    }).catch((error) => console.error('No fue posible retirar los recordatorios de sincronización mostrados.', error));
  }
}

function enqueueReminderOperation(operation: () => Promise<void>): Promise<void> {
  reminderOperations = reminderOperations
    .catch(() => undefined)
    .then(operation);
  return reminderOperations;
}

function isSupportedNativePlatform(): boolean {
  return Capacitor.isNativePlatform() && ['android', 'ios'].includes(Capacitor.getPlatform());
}
